import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as THREE from "three";
import { Pause, Play, X, Camera } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { useT } from "@/lib/i18n";
import { setCursor } from "@/lib/cursor";
import { axisEnd, interp, posAt } from "@/lib/telemetry";
import { buildScene, llToXY, localXY, placeCamera, placeKart } from "@/components/telemetry/onboard/scene";
import { useTracks } from "@/lib/useTracks";
import { DEFAULT_ALIGN, hasGeo, projectTrace } from "@/lib/geo";

const SPEEDS = [0.25, 0.5, 1, 2];
const btn = (on) => `px-2.5 py-1 font-mono text-[11px] uppercase border rounded-sm transition-colors duration-200 ${on ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10" : "border-[#282D3D] text-slate-300 hover:text-white"}`;

function kinematics(lap, xy, time) {
  const t0 = lap.t[0], end = axisEnd(lap, "t"), tt = Math.min(Math.max(time, t0), end);
  const at = (x) => posAt(lap, xy, Math.min(Math.max(x, t0), end)) || xy[0];
  const p = at(tt), a = at(tt - 0.3), b = at(tt + 0.3);
  return { p, ahead: [p[0] + (b[0] - a[0]) * 3, p[1] + (b[1] - a[1]) * 3] };
}

function useOnboardLoop(wrap, data, laps, st, axis, lapA, setTime) {
  useEffect(() => {
    const el = wrap.current;
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    renderer.domElement.style.cssText = "width:100%;height:100%;display:block";
    el.appendChild(renderer.domElement);
    const { scene, karts, ghosts, sun } = buildScene(data.xyA, data.xyB, renderer);
    const cams = laps.map((_, k) => {
      const c = new THREE.PerspectiveCamera(68, 1, 0.12, 1500);
      c.layers.enable(k + 1);
      return c;
    });
    let raf, last = performance.now(), lastUi = 0, size = "";
    const frame = (now) => {
      const s = st.current;
      const dt = Math.min(0.1, (now - last) / 1000);
      if (s.playing) {
        s.t += dt * s.speed;
        if (s.t > data.end) s.t = 0;
      }
      last = now;
      const w = el.clientWidth, h = el.clientHeight, vw = w / laps.length;
      if (size !== `${w}x${h}`) {
        size = `${w}x${h}`;
        renderer.setSize(w, h, false);
      }
      laps.forEach(([lap, xy], k) => {
        const { p, ahead } = kinematics(lap, xy, s.t);
        placeKart(karts[k], p, ahead);
        if (ghosts[k]) placeKart(ghosts[k], p, ahead);
        placeCamera(cams[k], p, ahead, s.mode, dt);
        karts[k].userData.driver.visible = s.mode !== "onboard";
      });
      ghosts.forEach((g, j) => { g.visible = g.position.distanceTo(cams[1 - j].position) > (s.mode === "onboard" ? 3 : 1.2); });
      sun.target.position.copy(karts[0].position);
      sun.position.copy(karts[0].position).add(new THREE.Vector3(30, 60, 20));
      renderer.setScissorTest(true);
      laps.forEach((_, k) => {
        cams[k].aspect = vw / h;
        cams[k].updateProjectionMatrix();
        renderer.setViewport(k * vw, 0, vw, h);
        renderer.setScissor(k * vw, 0, vw, h);
        renderer.render(scene, cams[k]);
      });
      if (now - lastUi > 100) {
        lastUi = now;
        setTime(s.t);
        setCursor(axis === "t" ? s.t : interp(lapA.t, lapA.d, Math.min(s.t, axisEnd(lapA, "t"))));
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      renderer.dispose();
      el.removeChild(renderer.domElement);
      setCursor(null);
    };
  }, [wrap, data, laps, st, axis, lapA, setTime]);
}

const Hud = ({ lap, lapA, time, color, side, chs, name }) => {
  const tt = Math.min(Math.max(time, lap.t[0]), axisEnd(lap, "t"));
  const val = (c, dec) => (c ? (interp(lap.t, lap.ch[c] || [], tt) ?? 0).toFixed(dec) : "—");
  let delta = null;
  if (side === "b") {
    const dA = interp(lapA.t, lapA.d, Math.min(time, axisEnd(lapA, "t")));
    const tB = interp(lap.d, lap.t, Math.min(dA, axisEnd(lap, "d")));
    if (tB != null) delta = tB - Math.min(time, axisEnd(lapA, "t"));
  }
  return (
    <div data-testid={`onboard-hud-${side}`} className="absolute top-3 bg-[#090A0F]/80 border rounded-sm px-3 py-2 font-mono text-xs space-y-0.5 pointer-events-none" style={{ borderColor: color, [side === "a" ? "left" : "right"]: 12 }}>
      <div className="font-bold truncate max-w-[260px]" style={{ color }}>{lap.label || name}</div>
      <div className="text-white text-lg font-bold tabular-nums">{tt.toFixed(2)} s</div>
      <div className="text-slate-300">{val(chs.sp, 0)} km/h · {val(chs.rpm, 0)} rpm</div>
      {chs.gl && <div className="text-slate-400">G lat {val(chs.gl, 2)}</div>}
      {delta != null && <div className={`font-bold ${delta > 0 ? "text-[#FF3B30]" : "text-[#10B981]"}`}>Δ {delta > 0 ? "+" : ""}{delta.toFixed(3)} s</div>}
    </div>
  );
};

export const Onboard3D = ({ lapA, lapB, axis, trackId, onClose }) => {
  const { t } = useT();
  const { tracks } = useTracks();
  const track = tracks.find((x) => x.id === trackId);
  const wrap = useRef(null);
  const st = useRef({ t: 0, playing: true, speed: 1, mode: "onboard" });
  const [ui, setUi] = useState({ playing: true, speed: 1, mode: "onboard" });
  const [time, setTime] = useState(0);
  const data = useMemo(() => {
    const geo = (lap) => {
      if (!lap) return null;
      if (lap.lat?.length) return lap.lat.map((la, i) => [la, lap.lon[i]]);
      return hasGeo(track) && lap.x?.length ? projectTrace(lap, track, { ...DEFAULT_ALIGN, ...(track.align || {}) }) : null;
    };
    const gA = geo(lapA), gB = geo(lapB);
    const end = Math.max(axisEnd(lapA, "t"), lapB ? axisEnd(lapB, "t") : 0);
    if (gA && (gB || !lapB)) return { xyA: llToXY(gA, gA[0]), xyB: gB ? llToXY(gB, gA[0]) : null, end, aligned: true };
    const origin = lapA.lat?.length ? [lapA.lat[0], lapA.lon[0]] : null;
    return { xyA: localXY(lapA, origin), xyB: lapB ? localXY(lapB, lapB.lat?.length ? origin : null) : null, end, aligned: false };
  }, [lapA, lapB, track]);
  const laps = useMemo(() => [[lapA, data.xyA], ...(lapB ? [[lapB, data.xyB]] : [])], [lapA, lapB, data]);
  const chs = useMemo(() => {
    const keys = Object.keys(lapA.ch);
    const f = (rx) => keys.find((c) => rx.test(c));
    return { sp: f(/speed/i), rpm: f(/rpm/i), gl: f(/^g[ _]?lat/i) };
  }, [lapA]);
  useOnboardLoop(wrap, data, laps, st, axis, lapA, setTime);
  const set = (k, v) => { st.current[k] = v; setUi((u) => ({ ...u, [k]: v })); };
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === " ") { e.preventDefault(); set("playing", !st.current.playing); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div data-testid="onboard-3d" className="fixed inset-0 z-[80] bg-[#090A0F] flex flex-col">
      <div className="relative flex-1 min-h-0">
        <div ref={wrap} data-testid="onboard-canvas" className="absolute inset-0" />
        {lapB && <div className="absolute inset-y-0 left-1/2 w-px bg-[#282D3D] pointer-events-none" />}
        <Hud lap={lapA} lapA={lapA} time={time} color="#3B82F6" side="a" chs={chs} name={`${t("lap_col")} ${lapA.lap}`} />
        {lapB && <Hud lap={lapB} lapA={lapA} time={time} color="#FF3B30" side="b" chs={chs} name={`${t("lap_col")} ${lapB.lap}`} />}
      </div>
      <div className="flex flex-wrap items-center gap-3 border-t border-[#282D3D] px-4 py-3">
        <button type="button" data-testid="onboard-play-btn" onClick={() => set("playing", !ui.playing)} className="p-2 rounded-sm bg-[#FFB800] text-[#090A0F]">
          {ui.playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        {SPEEDS.map((s) => <button type="button" key={s} data-testid={`onboard-speed-${s}`} onClick={() => set("speed", s)} className={btn(ui.speed === s)}>{s}x</button>)}
        <span className="w-px h-6 bg-[#282D3D]" />
        <Camera className="w-4 h-4 text-slate-400" />
        <button type="button" data-testid="onboard-cam-onboard" onClick={() => set("mode", "onboard")} className={btn(ui.mode === "onboard")}>{t("cam_onboard")}</button>
        <button type="button" data-testid="onboard-cam-chase" onClick={() => set("mode", "chase")} className={btn(ui.mode === "chase")}>{t("cam_chase")}</button>
        <Slider data-testid="onboard-time-slider" min={0} max={data.end} step={data.end / 1000} value={[Math.min(time, data.end)]}
          onValueChange={([v]) => { st.current.t = v; setTime(v); }} className="flex-1 min-w-[160px]" />
        <span className="font-mono text-xs text-[#FFB800] w-16 text-right">{time.toFixed(2)} s</span>
        <span data-testid="onboard-aligned-badge" className={`font-mono text-[10px] uppercase ${data.aligned ? "text-[#10B981]" : "text-slate-500"}`}>{data.aligned ? t("onboard_aligned") : t("onboard_local")}</span>
        <button type="button" data-testid="onboard-close-btn" onClick={onClose} className="p-2 border border-[#282D3D] rounded-sm text-slate-300 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
    </div>,
    document.body,
  );
};
